import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from 'antd';
import { Tooltip } from 'react-tooltip';

import { TaskOpenModalContext, LoansViewContext } from '../../../Private';
import { Loan, Task, TaskState } from '../../../../../gql/graphql';
import { TOOLTIP_STATES } from '../../../../../@common/utils/constants/tooltip-states.constant';
import { useRedirectToDashboard } from '@common/utils/hooks/useRedirectToDashboard';

import StateTag from '../../../../../components/Atoms/Tags/StateTag/StateTag';
import { Progress } from '../../../../../components/Atoms/Progress/Progress/Progress';
import { ModuleKeys } from '../../../../../services/task/modules-keys.constanst';
import { formatterDateProcessing } from '@common/utils/formatters/formatter';
import { getLoanProgress } from './mappers/loanProgressMapper';
import { getTasksByPhases } from './mappers/tasksByPhasesMapper';

interface LoanCardProps {
  loan: Loan;
}

const getStateLoan = (loan: Loan) => {
  if (loan.progress?.find(item => item.state === 'error')) {
    return { key: 'error', label: 'NEEDS ATTENTION' };
  }
  return { key: 'incomplete', label: 'IN PROGRESS' };
};

const countTaskIncomplete = (tasks: Task[]) =>
  tasks.filter(task =>
    task.state !== TaskState.Approved && task.state !== TaskState.Review
  ).length;

const isDraftOrRequestError = (task: Task) =>
  task.state === TaskState.Draft || task.state === TaskState.RequestError;

const shouldOpenTask = (task: Task) => {
  if (task.key === ModuleKeys.TITLE_COMPANY) {
    return isDraftOrRequestError(task) || (task.metadata?.titleCompany ?? false);
  }
  if (task.key === ModuleKeys.PAY_APPRAISAL) {
    return isDraftOrRequestError(task);
  }
  return task.state !== TaskState.ComingSoon;
};

const LoanCard = ({ loan }: LoanCardProps) => {
  const { setHandler: setTaskOpenModal }: any = useContext(TaskOpenModalContext);
  const { setHandlerVisible }: any = useContext(LoansViewContext);
  const { pathname } = useLocation();
  const { redirectToDashboard } = useRedirectToDashboard();
  const currentLoanId = pathname.startsWith('/home/')
    ? pathname.replace(/^\/home\//, '').replace(/\/$/, '')
    : undefined;

  const redirectLoan = async () => {
    await redirectToDashboard(loan._id);

    if(typeof setHandlerVisible === 'function')
      setHandlerVisible(false);
  };

  const redirectTask = async (task: Task) => {
    if (typeof setTaskOpenModal === 'function' && currentLoanId === loan._id) {
      setTaskOpenModal(task.key || task._id);
      setHandlerVisible(false);
      return;
    }
    await redirectToDashboard(loan._id, { taskKey: task.key || task._id });
  };

  const tasksNotHidden = loan.tasks.filter((task: Task) => !task.hidden);
  const tasksForRender = tasksNotHidden.map((task: Task) => {
    if (task.key === ModuleKeys.INSURANCE && task.metadata?.contactedApproved && task.state === TaskState.Approved) {
      const hasLoanDoc = (loan as any).loanDocs?.some((item: any) => item.key === 'insurance');
      return hasLoanDoc ? task : { ...task, state: TaskState.Review };
    }
    return task;
  });

  const phasesList = getTasksByPhases(tasksForRender);
  const stateLoan = getStateLoan(loan);
  const closingDate = loan?.salesforceData?.closingDate
    ? formatterDateProcessing(loan.salesforceData.closingDate)
    : null;
  const closingScheduled = loan?.salesforceData?.closingScheduled;

  return (
    <div
      className={`card-loan card-loan-${stateLoan.key} card-loan-${loan?.state}`}
    >
      <div className='card-loan-pre-header'>
        {loan?.state !== 'dead' ? (
          <StateTag
            isOtherState
            state={stateLoan.label}
            colorState={stateLoan.key === 'error' ? TaskState.RequestError : TaskState.Incomplete}
          />
        ) : (
          <StateTag isOtherState state="ARCHIVED" customClass="state-tag-dead" />
        )}
        {loan?.state !== 'dead' && (
          <div className='card-loan-pre-header-description'>
            <p>
              {closingScheduled ? 'Closing Date:' : 'Est. Closing:'}
              {closingDate ? (
                <b> {closingDate}</b>
              ) : (
                <b> PENDING</b>
              )}
            </p>
          </div>
        )}
      </div>

      <div className='card-loan-header'>
        <h3>{loan.name}</h3>
        <div className='card-loan-header-description'>
          <p>{loan.type} | {loan.purpose}</p>
        </div>
      </div>

      <div className='card-loan-content'>
        {phasesList.map((phase: any) => (
          <div key={phase.key} className='card-loan-content-documents'>
            <div className='card-loan-content-resume'>
              <label>{phase.label}</label>
              <span>
                {countTaskIncomplete(phase.tasks)}/{phase.tasks.length} incomplete tasks
              </span>
            </div>
            <div className='doc-states'>
              <div className='circles-tags'>
                {phase.tasks.map((task: Task) => (
                  <div key={task._id}>
                    <div
                      data-tooltip-id={`circle-state-${task._id}`}
                      className={`circle circle-${task.state} ${loan?.state !== 'dead' ? 'cursor' : ''}`}
                    />
                    {loan?.state !== 'dead' && (
                      <Tooltip
                        id={`circle-state-${task._id}`}
                        openOnClick
                        clickable
                        className={`circle-state-tooltip circle-state-tooltip-${task.state}`}
                      >
                        <div>
                          <b>{TOOLTIP_STATES[task.state]}</b>
                          <br />
                          <span>{task.name}</span>

                          {shouldOpenTask(task) && (
                            <>
                              <br /><br />
                              <b
                                style={{ cursor: 'pointer' }}
                                onClick={() => redirectTask(task)}
                              >
                                Go to task &#8594;
                              </b>
                            </>
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className='card-loan-bottom'>
        <div className='card-loan-content-documents'>
          <div className='card-loan-content-resume'>
            <label>Loan progress</label>
            <Progress bars={getLoanProgress(loan)} />
          </div>
        </div>
        <Button
          disabled={loan?.state === 'dead'}
          className="button button-link-border card-loan-button"
          onClick={redirectLoan}
        >
          Continue loan
        </Button>
      </div>
    </div>
  );
};

export default LoanCard;
